import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { adminKycAPI, profileAPI } from "@/services/api";
import { getErrorMessage } from "@/utils/errorHelpers";
import { Banknote, Camera, Download, KeyRound, Landmark, ShieldAlert, ShieldCheck, Trash2, Upload, User, Wifi } from "lucide-react";
import { toast } from "sonner";

function formatAddress(address = {}) {
  return [
    address.rue,
    address.numéro,
    address.commune,
    address.ville,
    address.province,
    address.pays,
  ].filter(Boolean).join(", ") || "-";
}

function Field({ label, value }) {
  return (
    <div className="rounded-lg border border-[#E6E4E0] bg-white px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#8A8F8C]">{label}</p>
      <p className="mt-1 text-sm font-medium text-[#1A1D1C] break-words">{value || "-"}</p>
    </div>
  );
}

export default function ProfilePage({ user }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoPosition, setPhotoPosition] = useState({ x: 50, y: 50 });
  const [photoZoom, setPhotoZoom] = useState(1);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [profilePhotoBroken, setProfilePhotoBroken] = useState(false);
  const [profilePhotoVersion, setProfilePhotoVersion] = useState("");
  const photoInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    profileAPI.getMe()
      .then((res) => {
        if (!cancelled) setProfile(res.data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <div className="py-10 text-[#5C625F]">Chargement du profil...</div>;
  }

  const currentUser = profile?.user || user;
  const kyc = profile?.kyc;
  const identity = kyc?.identity || {};
  const financial = kyc?.financial || {};
  const loginEvents = profile?.login_events || [];
  const hasAgreement = kyc?.status === "approved" && currentUser?.is_verified;
  const profilePhotoUrl = profileAPI.getPhotoUrl(profilePhotoVersion || currentUser?.profile_photo_updated_at || currentUser?.profile_photo_path || kyc?.profile_photo_path || "");

  const openPhotoEditor = (file) => {
    if (!file?.type?.startsWith("image/")) {
      toast.error("Selectionnez une image JPG, PNG ou WebP");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("La photo ne peut pas depasser 8 Mo");
      return;
    }
    setPhotoPreview(URL.createObjectURL(file));
    setPhotoPosition({ x: 50, y: 50 });
    setPhotoZoom(1);
    setPhotoOpen(true);
  };

  const saveProfilePhoto = async () => {
    if (!photoPreview) return;
    setSavingPhoto(true);
    try {
      const source = new Image();
      source.src = photoPreview;
      await new Promise((resolve, reject) => {
        source.onload = resolve;
        source.onerror = reject;
      });
      const size = 512;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext("2d");
      const baseScale = Math.max(size / source.naturalWidth, size / source.naturalHeight);
      const scale = baseScale * photoZoom;
      const width = source.naturalWidth * scale;
      const height = source.naturalHeight * scale;
      context.drawImage(source, (size - width) * (photoPosition.x / 100), (size - height) * (photoPosition.y / 100), width, height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
      if (!blob) throw new Error("Conversion image impossible");
      const formData = new FormData();
      formData.append("file", new File([blob], "photo-profil.jpg", { type: "image/jpeg" }));
      const response = await profileAPI.updatePhoto(formData);
      setProfilePhotoVersion(response.data?.profile_photo_path || String(Date.now()));
      setProfilePhotoBroken(false);
      setPhotoOpen(false);
      setPhotoPreview("");
      toast.success("Photo de profil mise a jour");
    } catch (error) {
      toast.error(getErrorMessage(error, "Mise a jour de la photo impossible"));
    } finally {
      setSavingPhoto(false);
    }
  };

  const requestDeletion = async () => {
    setDeleting(true);
    try {
      const response = await profileAPI.requestAccountDeletion({ reason: deleteReason });
      toast.success(response.data?.message || "Demande de suppression envoyee");
      setDeleteOpen(false);
      setDeleteReason("");
    } catch (error) {
      toast.error(getErrorMessage(error, "Demande de suppression impossible"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="py-8 space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#2A4B46] font-['Outfit']">Mon compte</h1>
          <p className="mt-1 text-sm text-[#5C625F]">Profil personnel, coordonnées financières et sécurité.</p>
        </div>
        <div className="flex items-center gap-3 self-start md:self-auto">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#2A4B46] text-sm font-semibold text-white">
            {!profilePhotoBroken ? (
              <img src={profilePhotoUrl} alt="Photo de profil" className="h-full w-full object-cover" onError={() => setProfilePhotoBroken(true)} />
            ) : currentUser?.full_name?.slice(0, 2).toUpperCase()}
          </div>
          <Badge className="w-fit rounded-full bg-[#D4A373] text-white">{currentUser?.id}</Badge>
        </div>
      </div>

      <Card className="border-[#E6E4E0] shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#2A4B46]">
            <User size={20} /> Identité ARTICO
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          <Field label="Nom complet" value={currentUser?.full_name} />
          <Field label="Email" value={currentUser?.email} />
          <Field label="Niveau" value={currentUser?.partner_level || currentUser?.role} />
          <Field label="Points ascension" value={currentUser?.points_ascension ?? 0} />
          <Field label="Nom KYC" value={[identity.nom, identity.postnom, identity.prenom].filter(Boolean).join(" ")} />
          <Field label="Téléphone" value={identity.telephone} />
          <Field label="N° identité" value={identity.numero_identite} />
          <Field label="Adresse" value={formatAddress(identity.adresse)} />
        </CardContent>
        {currentUser?.role === "partner" && (
          <CardContent className="pt-0">
            <Button type="button" variant="outline" className="gap-2 border-[#E6E4E0] text-[#2A4B46] hover:bg-[#F3EFE9]" onClick={() => photoInputRef.current?.click()}>
              <Camera size={17} /> Modifier ma photo de profil
            </Button>
            <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(event) => openPhotoEditor(event.target.files?.[0])} />
          </CardContent>
        )}
      </Card>

      <Dialog open={photoOpen} onOpenChange={setPhotoOpen}>
        <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#2A4B46]"><Camera size={19} /> Ajuster la photo de profil</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="mx-auto h-56 w-56 overflow-hidden rounded-full bg-[#F3EFE9] ring-4 ring-[#E6E4E0]">
              {photoPreview && <img src={photoPreview} alt="Apercu de la photo de profil" className="h-full w-full object-cover" style={{ objectPosition: `${photoPosition.x}% ${photoPosition.y}%`, transform: `scale(${photoZoom})` }} />}
            </div>
            <div className="space-y-3 text-sm text-[#5C625F]">
              <p>Centrez votre visage dans le cercle avant d'enregistrer.</p>
              <label className="block">Position horizontale<input aria-label="Position horizontale" type="range" min="0" max="100" value={photoPosition.x} onChange={(event) => setPhotoPosition((value) => ({ ...value, x: Number(event.target.value) }))} className="mt-1 w-full" /></label>
              <label className="block">Position verticale<input aria-label="Position verticale" type="range" min="0" max="100" value={photoPosition.y} onChange={(event) => setPhotoPosition((value) => ({ ...value, y: Number(event.target.value) }))} className="mt-1 w-full" /></label>
              <label className="block">Zoom<input aria-label="Zoom" type="range" min="1" max="2" step="0.05" value={photoZoom} onChange={(event) => setPhotoZoom(Number(event.target.value))} className="mt-1 w-full" /></label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button type="button" variant="outline" className="gap-2" onClick={() => photoInputRef.current?.click()}><Upload size={16} /> Changer</Button>
              <Button type="button" className="bg-[#2A4B46] text-white hover:bg-[#1f3a36]" onClick={saveProfilePhoto} disabled={savingPhoto}>{savingPhoto ? "Envoi..." : "Enregistrer"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Card className="border-[#E6E4E0] shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#2A4B46]">
            <Banknote size={20} /> Informations bancaires
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          <Field label="Mobile Money - Intitulé" value={financial.mobile_money_intitule} />
          <Field label="Mobile Money - Numéro" value={financial.mobile_money_numero} />
          <Field label="Banque" value={financial.banque_nom} />
          <Field label="Succursale" value={financial.banque_succursale} />
          <Field label="Intitulé bancaire" value={financial.banque_intitule} />
          <Field label="N° compte" value={financial.banque_numero} />
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#2A4B46]">
              <ShieldCheck size={20} /> Sécurité et connexions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/forgot-password">
              <Button className="w-full justify-start gap-2 bg-[#2A4B46] text-white hover:bg-[#1f3a36]">
                <KeyRound size={18} /> Modifier le mot de passe par email
              </Button>
            </Link>
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full justify-start gap-2 border-red-200 text-red-700 hover:bg-red-50">
                  <Trash2 size={18} /> Demander la suppression du compte
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[92dvh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-700">
                    <ShieldAlert size={19} /> Suppression de compte
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-sm leading-6 text-[#424846]">
                  <p>
                    Cette action transmet une demande aux administrateurs. Les donnees supprimables seront traitees; les donnees necessaires aux audits, transactions, bonus, securite ou obligations legales peuvent etre conservees.
                  </p>
                  <div>
                    <label className="text-sm font-semibold text-[#2A4B46]">Motif ou precision</label>
                    <Textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} rows={4} className="mt-1" />
                  </div>
                  <Button onClick={requestDeletion} disabled={deleting} className="w-full bg-red-700 text-white hover:bg-red-800">
                    {deleting ? "Envoi..." : "Confirmer la demande"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            {loginEvents.length === 0 ? (
              <p className="text-sm text-[#5C625F]">Aucune connexion récente.</p>
            ) : (
              loginEvents.map((event) => (
                <div key={event.id} className="rounded-lg border border-[#E6E4E0] bg-white px-4 py-3">
                  <p className="flex items-center gap-2 text-sm font-semibold text-[#1A1D1C]">
                    <Wifi size={16} className="text-[#D4A373]" /> {event.ip_address || "IP inconnue"}
                  </p>
                  <p className="mt-1 text-xs text-[#5C625F]">
                    {event.created_at ? new Date(event.created_at).toLocaleString("fr-FR") : "Date inconnue"}
                  </p>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#2A4B46]">
              <Landmark size={20} /> Mes documents
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-lg border border-[#E6E4E0] bg-white px-4 py-3">
              <p className="text-sm font-semibold text-[#1A1D1C]">Agrément ARTICO</p>
              <p className="mt-1 text-xs text-[#5C625F]">
                {hasAgreement ? "Disponible au téléchargement." : "Disponible après validation du KYC."}
              </p>
            </div>
            <Link to="/documents">
              <Button variant="outline" className="w-full gap-2 border-[#E6E4E0] text-[#2A4B46] hover:bg-[#F3EFE9]">
                <Landmark size={18} /> Ouvrir mes documents
              </Button>
            </Link>
            {hasAgreement ? (
              <a href={adminKycAPI.getAgreementPdfUrl(kyc.id)} target="_blank" rel="noreferrer">
                <Button className="w-full gap-2 bg-[#D4A373] text-white hover:bg-[#c08d5c]">
                  <Download size={18} /> Télécharger mon agrément
                </Button>
              </a>
            ) : currentUser?.role === "partner" ? (
              <Link to="/verification">
                <Button variant="outline" className="w-full gap-2 border-orange-200 text-orange-800 hover:bg-orange-50">
                  <ShieldCheck size={18} /> Vérifier mon identité
                </Button>
              </Link>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
